import { computed, inject, Injectable, signal } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service, rxResourceFixed } from '@zayan-erp/shared';
import {
  DsmApplyAddendumIncentivePayload,
  DsmAddendumIncentivePreview,
  DsmAddendumIncentivePreviewPayload,
  DsmLotIncentive,
  DsmLotIncentiveSummary
} from '../models/dsm-lot-incentives.models';
import { map, Observable, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DsmLotIncentiveService {
  #apiV2 = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #baseUrl = this.#apiConfig.baseUrlV2;

  #lotId = signal<string | null>(null);
  #dealId = signal<string | null>(null);

  fetchLotIncentive(lotId: string, dealId: string) {
    this.#lotId.set(lotId);
    this.#dealId.set(dealId);
  }

  resetLotIncentive() {
    this.#lotId.set(null);
    this.#dealId.set(null);
  }

  reloadLotIncentive() {
    this.#lotIncentivesResource.reload();
  }

  #lotIncentivesResource = rxResourceFixed<DsmLotIncentive | null, { lotId: string | null; dealId: string | null }>({
    params: () => ({ lotId: this.#lotId(), dealId: this.#dealId() }),
    stream: ({ params }) =>
      params.lotId && params.dealId ? this.#getLotIncentiveDetails(params.lotId, params.dealId) : of(null)
  });

  isIncentiveLoading = computed(() => this.#lotIncentivesResource.isLoading());
  incentiveError = computed(() => this.#lotIncentivesResource.error());
  #incentive = computed(() => this.#lotIncentivesResource.value());
  incentiveTotal = computed(() => this.#incentive()?.total ?? 0);
  incentiveApplied = computed(() => this.#incentive()?.used ?? 0);
  incentiveRemaining = computed(() => this.#incentive()?.remaining ?? 0);
  incentivePercentageUsed = computed(() => Math.round((this.incentiveApplied() / this.incentiveTotal()) * 100) ?? 0);

  #getLotIncentiveDetails(lotId: string, dealId: string): Observable<DsmLotIncentive> {
    return this.#apiV2
      .getData<DsmLotIncentiveSummary>(`${this.#baseUrl}/addendums/incentive-summary?lotId=${lotId}&dealId=${dealId}`, {
        showLoader: false,
        showMessage: false,
        showError: false
      })
      .pipe(
        map((summary: DsmLotIncentiveSummary) => ({ lotId, ...summary })),
        tap(summary => console.log('summary', summary))
      );
  }

  previewIncentiveApplication(
    addendumId: string,
    payload: DsmAddendumIncentivePreviewPayload
  ): Observable<DsmAddendumIncentivePreview> {
    return this.#apiV2.postData<DsmAddendumIncentivePreview>(
      `${this.#baseUrl}/addendums/${addendumId}/apply-incentive/preview`,
      { showLoader: false, showMessage: false, showError: false },
      payload
    );
  }

  applyIncentive(addendumId: string, payload: DsmApplyAddendumIncentivePayload): Observable<void> {
    return this.#apiV2.postData<void>(
      `${this.#baseUrl}/addendums/${addendumId}/apply-incentive`,
      { showLoader: true, showMessage: true, showError: true },
      payload
    );
  }
}
